import * as Menu from '@radix-ui/react-dropdown-menu';
import {
  Building2,
  CalendarCheck,
  ChevronDown,
  ClipboardList,
  Contact,
  FileInput,
  LayoutDashboard,
  LogOut,
  Monitor,
  Moon,
  Network,
  Palette,
  ScrollText,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Sun,
  UserCheck,
  UserRound,
  Users,
  type LucideIcon,
  BellRing,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { useAuth, useCan } from '../features/auth/AuthProvider';
import { useTheme, type ThemePreference } from '../hooks/useTheme';
import { cn } from '../utils/cn';

const PLATFORM_NAME = import.meta.env.VITE_PLATFORM_NAME || 'LeadDesk';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  visible?: boolean;
}

const themeOptions: { value: ThemePreference; label: string; icon: LucideIcon }[] = [
  { value: 'light', label: 'Light theme', icon: Sun },
  { value: 'dark', label: 'Dark theme', icon: Moon },
  { value: 'system', label: 'System theme', icon: Monitor },
];

function ThemeSwitcher() {
  const { preference, setPreference } = useTheme();
  return (
    <div role="radiogroup" aria-label="Theme" className="flex rounded-md bg-surface-sunken p-0.5">
      {themeOptions.map(({ value, label, icon: Icon }) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={preference === value}
          aria-label={label}
          title={label}
          onClick={() => setPreference(value)}
          className={cn(
            'flex h-7 flex-1 items-center justify-center rounded',
            preference === value ? 'bg-surface text-fg shadow-xs' : 'text-fg-subtle hover:text-fg',
          )}
        >
          <Icon aria-hidden className="size-4" />
        </button>
      ))}
    </div>
  );
}

function UserMenu({ accountPath }: { accountPath: string }) {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  if (!me) return null;
  const initials = me.user.fullName
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
  return (
    <Menu.Root>
      <Menu.Trigger className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-surface-sunken">
        <span
          aria-hidden
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-800"
        >
          {initials}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-fg">{me.user.fullName}</span>
          <span className="block truncate text-xs text-fg-subtle">{me.user.email}</span>
        </span>
        <ChevronDown aria-hidden className="size-4 text-fg-subtle" />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content
          align="start"
          sideOffset={6}
          className="z-50 min-w-52 rounded-md border border-line bg-surface p-1 shadow-md"
        >
          <Menu.Item
            onSelect={() => navigate(accountPath)}
            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm outline-none data-highlighted:bg-surface-sunken"
          >
            <UserRound aria-hidden className="size-4" /> My account
          </Menu.Item>
          <Menu.Separator className="my-1 h-px bg-line" />
          <Menu.Item
            onSelect={() =>
              void logout().then(() => navigate(me.user.type === 'platform' ? '/platform/login' : '/login'))
            }
            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-danger outline-none data-highlighted:bg-danger-soft"
          >
            <LogOut aria-hidden className="size-4" /> Sign out
          </Menu.Item>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}

/** Generic sidebar shell used by the tenant workspace and the platform console. */
export function Shell({
  brand,
  nav,
  accountPath,
  banner,
}: {
  brand: ReactNode;
  nav: NavItem[];
  accountPath: string;
  banner?: ReactNode;
}) {
  return (
    <div className="flex min-h-dvh flex-col md:flex-row">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-surface focus:px-3 focus:py-2"
      >
        Skip to content
      </a>
      <aside className="flex shrink-0 flex-col border-b border-line bg-surface md:sticky md:top-0 md:h-dvh md:w-64 md:border-b-0 md:border-r">
        <div className="flex h-14 items-center gap-2 px-4">{brand}</div>
        <nav
          aria-label="Main"
          className="flex gap-1 overflow-x-auto px-2 pb-2 md:flex-1 md:flex-col md:overflow-y-auto md:pb-0"
        >
          {nav
            .filter((item) => item.visible !== false)
            .map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end ?? false}
                className={({ isActive }) =>
                  cn(
                    'flex h-9 shrink-0 items-center gap-2 rounded-md px-3 text-sm font-medium',
                    isActive
                      ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200'
                      : 'text-fg-muted hover:bg-surface-sunken hover:text-fg',
                  )
                }
              >
                <Icon aria-hidden className="size-4" />
                {label}
              </NavLink>
            ))}
        </nav>
        <div className="hidden space-y-2 border-t border-line p-3 md:block">
          <UserMenu accountPath={accountPath} />
          <ThemeSwitcher />
        </div>
      </aside>
      <main id="main" className="min-w-0 flex-1">
        {banner}
        <Outlet />
      </main>
    </div>
  );
}

export function AppShell() {
  const { me } = useAuth();
  const can = useCan();
  const name = me?.branding?.displayName ?? me?.tenant?.name ?? PLATFORM_NAME;
  const logo = me?.branding?.logoUrl;
  const onboarding = me?.tenant && me.tenant.onboardingStep !== 'done' && can('tenant.configure');

  const nav: NavItem[] = [
    { to: '/app', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/app/onboarding', label: 'Setup', icon: ClipboardList, visible: !!onboarding },
    { to: '/app/leads', label: 'Leads', icon: Contact, visible: can('leads.view') },
    { to: '/app/follow-ups', label: 'Follow-ups', icon: CalendarCheck, visible: can('followups.view') },
    {
      to: '/app/assignments',
      label: 'Assignments',
      icon: UserCheck,
      visible: [
        'assignments.assign',
        'assignments.view_workload',
        'transfers.approve',
        'leads.transfer',
      ].some((c) => can(c)),
    },
    { to: '/app/team/staff', label: 'Staff', icon: Users, visible: can('staff.view') },
    { to: '/app/team/branches', label: 'Branches', icon: Building2, visible: can('branches.view') },
    { to: '/app/team/departments', label: 'Teams', icon: Network, visible: can('departments.view') },
    { to: '/app/team/roles', label: 'Roles & permissions', icon: ShieldCheck, visible: can('roles.view') },
    {
      to: '/app/settings/business',
      label: 'Business settings',
      icon: Settings,
      visible: can('tenant.configure'),
    },
    { to: '/app/settings/branding', label: 'Branding', icon: Palette, visible: can('branding.configure') },
    {
      to: '/app/settings/leads',
      label: 'Lead settings',
      icon: SlidersHorizontal,
      visible: can('lead_config.view') || can('pipelines.view'),
    },
    {
      to: '/app/settings/alerts',
      label: 'Lead alerts',
      icon: BellRing,
      visible: can('tenant.configure') || can('notification_rules.configure') || can('templates.manage'),
    },
    { to: '/app/settings/forms', label: 'Website forms', icon: FileInput, visible: can('forms.configure') },
    { to: '/app/settings/audit', label: 'Audit log', icon: ScrollText, visible: can('audit.view') },
  ];

  return (
    <Shell
      accountPath="/app/account"
      nav={nav}
      brand={
        <>
          {logo ? (
            <img src={logo} alt="" className="size-8 rounded-md object-contain" />
          ) : (
            <div
              aria-hidden
              className="flex size-8 items-center justify-center rounded-md bg-brand-600 font-bold text-white"
            >
              {name.slice(0, 1).toUpperCase()}
            </div>
          )}
          <span className="truncate text-base font-semibold text-fg">{name}</span>
        </>
      }
    />
  );
}
